import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiPackage, FiStar, FiCheckCircle, FiXCircle, FiClock, FiShield, FiTrash2, FiEye, FiDownload, FiAlertTriangle } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import axios from 'axios';

function AdminPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [pendingExtensions, setPendingExtensions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');

    if (!token) {
      toast.error('Please login first');
      navigate('/login', { replace: true });
      return;
    }

    if (userRole !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard', { replace: true });
      return;
    }

    fetchAllData(token);
  }, [navigate]);

  const fetchAllData = async (token) => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, usersRes, extensionsRes, pendingRes] = await Promise.all([
        axios.get('https://extension.avalw.com/api/admin/stats', { headers }),
        axios.get('https://extension.avalw.com/api/admin/users', { headers }),
        axios.get('https://extension.avalw.com/api/admin/extensions', { headers }),
        axios.get('https://extension.avalw.com/api/admin/extensions/pending', { headers })
      ]);

      setStats(statsRes.data.data);
      setUsers(usersRes.data.data || []);
      setExtensions(extensionsRes.data.data || []);
      setPendingExtensions(pendingRes.data.data || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (extensionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `https://extension.avalw.com/api/admin/extensions/${extensionId}/status`,
        { status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Extension approved!');
      fetchAllData(token);
    } catch (error) {
      toast.error('Failed to approve extension');
    }
  };

  const handleReject = async (extensionId) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `https://extension.avalw.com/api/admin/extensions/${extensionId}/status`,
        { status: 'rejected', rejectionReason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Extension rejected');
      fetchAllData(token);
    } catch (error) {
      toast.error('Failed to reject extension');
    }
  };

  const handleBanUser = async (userId, currentlyBanned) => {
    const action = currentlyBanned ? 'unban' : 'ban';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `https://extension.avalw.com/api/admin/users/${userId}/ban`,
        { isBanned: !currentlyBanned, banReason: currentlyBanned ? null : 'Banned by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${action}ned successfully`);
      fetchAllData(token);
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FiShield className="text-red-600" /> Admin Panel
          </h1>
          <p className="text-gray-600 mt-1">Manage users, extensions, and reviews</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {['dashboard', 'users', 'extensions', 'pending'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium transition-all border-b-2 -mb-[2px] ${
                activeTab === tab
                  ? 'text-gray-900 border-gray-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {tab === 'dashboard' && 'Dashboard'}
              {tab === 'users' && `Users (${users.length})`}
              {tab === 'extensions' && `Extensions (${extensions.length})`}
              {tab === 'pending' && `Pending (${pendingExtensions.length})`}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={<FiUsers />}
                label="Total Users"
                value={stats.totalUsers}
                color="blue"
              />
              <StatCard
                icon={<FiPackage />}
                label="Total Extensions"
                value={stats.totalExtensions}
                color="green"
              />
              <StatCard
                icon={<FiClock />}
                label="Pending Review"
                value={stats.extensionsByStatus?.pending || 0}
                color="yellow"
              />
              <StatCard
                icon={<FiStar />}
                label="Total Reviews"
                value={stats.totalReviews}
                color="purple"
              />
            </div>

            {/* Detailed Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Users by Role */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Users by Role</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Regular Users</span>
                    <span className="font-bold text-gray-900">{stats.usersByRole?.users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Developers</span>
                    <span className="font-bold text-gray-900">{stats.usersByRole?.developers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Admins</span>
                    <span className="font-bold text-gray-900">{stats.usersByRole?.admins || 0}</span>
                  </div>
                </div>
              </div>

              {/* Extensions by Status */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Extensions by Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-3 h-3 rounded-full bg-gray-400"></span> Draft
                    </span>
                    <span className="font-bold text-gray-900">{stats.extensionsByStatus?.draft || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-yellow-600">
                      <span className="w-3 h-3 rounded-full bg-yellow-400"></span> Pending
                    </span>
                    <span className="font-bold text-gray-900">{stats.extensionsByStatus?.pending || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-green-600">
                      <span className="w-3 h-3 rounded-full bg-green-400"></span> Approved
                    </span>
                    <span className="font-bold text-gray-900">{stats.extensionsByStatus?.approved || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-red-600">
                      <span className="w-3 h-3 rounded-full bg-red-400"></span> Rejected
                    </span>
                    <span className="font-bold text-gray-900">{stats.extensionsByStatus?.rejected || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">User</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Joined</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          {user.avatar ? (
                            <img src={user.avatar} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <FiUsers className="text-gray-500" />
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{user.username}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin' ? 'bg-red-100 text-red-700' :
                        user.role === 'developer' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.isBanned ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Banned</span>
                      ) : user.isVerified ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Verified</span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Unverified</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => handleBanUser(user.id, user.isBanned)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            user.isBanned
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {user.isBanned ? 'Unban' : 'Ban'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Extensions Tab */}
        {activeTab === 'extensions' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Extension</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Author</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Downloads</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {extensions.map(ext => (
                  <tr key={ext.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {ext.icon ? (
                          <img src={ext.icon} alt="" className="w-10 h-10 rounded-lg" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <FiPackage className="text-gray-500" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{ext.name}</div>
                          <div className="text-sm text-gray-500">v{ext.version}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{ext.author?.username || 'Unknown'}</td>
                    <td className="px-6 py-4 text-gray-600">{ext.category}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        ext.status === 'approved' ? 'bg-green-100 text-green-700' :
                        ext.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        ext.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {ext.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{ext.downloadCount || 0}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <a
                          href={`https://extension.avalw.com/api/extensions/${ext.id}/download`}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="Download"
                        >
                          <FiDownload size={16} />
                        </a>
                        {ext.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(ext.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Approve"
                            >
                              <FiCheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => handleReject(ext.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <FiXCircle size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingExtensions.length === 0 ? (
              <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
                <FiCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                <p className="text-gray-600">No pending extensions to review</p>
              </div>
            ) : (
              pendingExtensions.map(ext => (
                <motion.div
                  key={ext.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {ext.icon ? (
                        <img src={ext.icon} alt="" className="w-16 h-16 rounded-xl" />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center">
                          <FiPackage className="text-2xl text-gray-500" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{ext.name}</h3>
                        <p className="text-gray-600 mt-1">{ext.description}</p>
                        <div className="flex gap-4 mt-3 text-sm text-gray-500">
                          <span>By: {ext.author?.username || 'Unknown'}</span>
                          <span>Category: {ext.category}</span>
                          <span>Version: {ext.version}</span>
                          <span>Size: {(ext.fileSize / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleApprove(ext.id)}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      <FiCheckCircle /> Approve
                    </button>
                    <button
                      onClick={() => handleReject(ext.id)}
                      className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                    >
                      <FiXCircle /> Reject
                    </button>
                    <a
                      href={`https://extension.avalw.com/api/extensions/${ext.id}/download`}
                      className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                    >
                      <FiDownload /> Download & Test
                    </a>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <div className={`w-12 h-12 ${colors[color]} rounded-lg flex items-center justify-center text-2xl mb-4`}>
        {icon}
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      <div className="text-gray-600 text-sm mt-1">{label}</div>
    </div>
  );
}

export default AdminPanel;
